/**
 * Contenido narrativo del BARRIO — la zona de sustento del juego (ver
 * `docs/biblia/escenarios.md`, sección "EL BARRIO"). TODO el texto vive
 * aquí como dato: la escena `BarrioScene` solo lo renderiza, no inventa
 * frases. Esto mantiene la separación dura motor/contenido.
 *
 * Convenciones:
 * - Tono callejero de colegas, humor GTA, sin nombres reales — solo apodos.
 * - Solo apodos de la biblia de personajes (`docs/biblia/personajes.md`).
 * - El "ciclo del barrio" (trabajar / dormir / paki / axeliki) vive aquí
 *   también como constantes de balance, para que un solo cambio aquí mueva
 *   toda la economía del barrio.
 */

// -----------------------------------------------------------------------
// Balance del barrio — precios y ganancias del ciclo
// -----------------------------------------------------------------------

/** Ganancia bruta por "Trabajar unas horas". Canon del encargo. */
export const TRABAJAR_GANANCIA = 25;
/** Máximo de veces que puedes trabajar en una misma visita al barrio.
 *  Al agotarlo, el botón muestra "ya has currado bastante hoy". */
export const TRABAJAR_MAX_POR_VISITA = 3;

/** Precio de la comida en el paki. Canon del encargo. */
export const COMIDA_PRECIO = 5;
/** Cantidad de comida que se suma al inventario por compra. */
export const COMIDA_CANTIDAD = 1;

/** Precio normal de la yerba del AXELIKI. Canon del encargo. */
export const YERBA_PRECIO = 15;
/** Precio rebajado si pillaste al AXELIKI "en el momento justo" — su
 *  impuntualidad a favor del jugador (canon: ronda final antes de irte). */
export const YERBA_PRECIO_REBAJADO = 12;
/** Cantidad de yerba que se suma al inventario por compra. */
export const YERBA_CANTIDAD = 1;

/** Cantidad de salud que añade dormir. El canon del encargo es "salud a 100". */
export const DORMIR_SALUD = 100;

/** Probabilidad (0-1) de que aparezca EL PROMO o EL AMIGO CIGARRO en
 *  patinete pidiendo un cigarro al TERMINAR cualquier acción. Canon. */
export const EVENTO_CIGARRO_PROB = 0.3;
/** Coste simbólico de "darle un cigarro": 1€. */
export const EVENTO_CIGARRO_COSTE = 1;

// -----------------------------------------------------------------------
// Descripciones de ambiente y copy del panel central
// -----------------------------------------------------------------------

/** Título fijo de la escena. */
export const BARRIO_TITULO = 'EL BARRIO';

/** Descripción corta que se muestra en el panel central al abrir la escena. */
export const BARRIO_DESCRIPCION =
  'Calle de tarde-noche. Farolas, persianas a medio bajar y un súper con luz fría.\nTrabaja, come, espera al AXELIKI o vuelve a dormir.';

// -----------------------------------------------------------------------
// Botonera principal — copy exacto de los 5 botones
// -----------------------------------------------------------------------

export interface BarrioAction {
  id: 'trabajar' | 'paki' | 'axeliki' | 'dormir';
  nombre: string;
  emoji: string;
  descripcion: string;
}

export const BARRIO_ACTIONS: BarrioAction[] = [
  {
    id: 'trabajar',
    nombre: 'Trabajar unas horas',
    emoji: '🛠️',
    descripcion: '+25€. Currar en lo que salga.',
  },
  {
    id: 'paki',
    nombre: 'Comprar comida en el paki',
    emoji: '🏪',
    descripcion: '5€. Tienda de barrio, luz fluorescente.',
  },
  {
    id: 'axeliki',
    nombre: 'Buscar al AXELIKI (yerba)',
    emoji: '🚗',
    descripcion: '15€. Da vueltas con el BMW. Siempre va tarde.',
  },
  {
    id: 'dormir',
    nombre: 'Dormir',
    emoji: '🛏️',
    descripcion: 'Recupera salud. Amaneces nuevo.',
  },
];

// -----------------------------------------------------------------------
// Frases de cada acción (panel corto después de tap)
// -----------------------------------------------------------------------

/** Frases cortas para "Trabajar unas horas" — se elige una al azar. */
export const FRASES_TRABAJAR: string[] = [
  'Te han dado el curro de cargar cajas en el súper del final. Sin quejas.',
  'Has echado cuatro horas repartiendo propaganda. El patrocinador paga bien.',
  'Una mañana de chapuzas en el local del primo. Sudor y pasta rápida.',
  'Has movido un par de muebles por el barrio. La espalda lo nota pero la cartera no.',
];

/** Frase fija cuando se agota el contador de trabajar por visita. */
export const FRASE_TRABAJAR_LIMITE =
  'Ya has currado bastante hoy. El cuerpo te pide descanso.';

/** Frases de "Comprar comida en el paki" cuando tienes dinero y sale bien. */
export const FRASES_PAKI_OK: string[] = [
  'Te pita el ticket. Comida para hoy, gracias.',
  'Una bolsa con lo básico. El del paki asiente con la cabeza.',
  'Has pillado lo justo. La nevera de la casa lo agradece.',
  'Compra rápida. Saluda, paga, sale. El del paki ya te conoce.',
];

/** Frases de "Comprar comida en el paki" cuando no llegas a los 5€. */
export const FRASES_PAKIO_SIN_DINERO: string[] = [
  'Hojeas la nevera pero hoy no llegas. Vuelve con dinero.',
  'El del paki te mira. Sabes que no cuela. Te vas con las manos vacías.',
  'Sin cinco euros no hay bolsa. La puerta del paki se te cierra sola.',
  'Te asomas y te acuerdas de la cartera. Vuelta a la calle.',
];

/** Mini-diálogo del tendero (EL DEL PAKI), en formato línea suelta con
 *  personalidad de "tendero funcional" del barrio. */
export const DIALOGOS_PAKI: { personajeId: 'el-del-paki'; texto: string }[] = [
  {
    personajeId: 'el-del-paki',
    texto: '¿Qué va a ser, chaval? Hoy hay lo de siempre.',
  },
  {
    personajeId: 'el-del-paki',
    texto: 'Vienes con prisa, ¿eh? Anda, toma la bolsa. Cinco euros.',
  },
  {
    personajeId: 'el-del-paki',
    texto: 'Tú siempre compras lo mismo. Un día vienes y me cambias la vida.',
  },
  {
    personajeId: 'el-del-paki',
    texto: 'A ver, a ver… sí, tengo de todo. Cuesta lo que cuesta. Cinco.',
  },
];

/** Frases al dormir — "panel de buenas noches". */
export const FRASES_DORMIR: string[] = [
  'Cierras los ojos. Mañana el barrio sigue ahí esperándote.',
  'Te duermes con el sonido de un patinete pasando de fondo. Amaneces nuevo.',
  'La farola de la calle parpadea fuera. Tú ya estás en otro lado.',
  'Una noche más. La salud vuelve a tope. Mañana será otro día.',
];

// -----------------------------------------------------------------------
// AXELIKI — el impuntual. La mecánica vive aquí como dato.
// -----------------------------------------------------------------------

/** Frase de apertura del AXELIKI — canon de la biblia de personajes. */
export const AXELIKI_FRASE_INICIAL = '5 minutos y estoy';

/** Frases de AXELIKI en cada ronda de espera (1-2 rondas). Tono cachondo,
 *  apelando a que "está al caer". */
export const AXELIKI_FRASES_ESPERA: string[] = [
  'Que estoy a dos calles. No me seas borde.',
  'Espera espera, que estoy repostando. Cinco minutos.',
  'El BMW va sin gasolina. Dame un segundo.',
  'Me he metido en un atasco de esos buenos. Calma.',
  'Acabo de salir de casa. Literal. Tres minutos.',
  'Que estoy aparcando en doble fila, ya bajo.',
];

/** Frases de AXELIKI cuando INSISTES (mecánica: responde con cachondeo
 *  y a veces tarda una ronda más). */
export const AXELIKI_FRASES_INSISTIR: string[] = [
  'Tío, pesado. Que sí, que estoy bajando.',
  'Joder, qué prisa tienes. Relájate, anda.',
  '¿Tú no descansas? Yo también vivo, ¿eh?',
  'Me estás agobiando. Te juro que estoy a punto.',
  'Deja de escribirme que me pones nervioso y tardo más.',
  'Que sí pesada, pesada. Ya te veo desde la esquina.',
];

/** Frase cuando por fin "llega el BMW" (ronda aleatoria). */
export const AXELIKI_FRASE_LLEGA =
  '¡Aquí estoy! Vaya tela. ¿Cuánto decías? Quince. Toma.';

/** Frase de AXELIKI cuando llegaste "en el momento justo" (ronda final,
 *  precio rebajado). Él sigue sin admitir que iba tarde. */
export const AXELIKI_FRASE_LLEGA_REBAJADO =
  '¡Eh, mira, que aún no he subido el precio! Córtate, anda. Doce pavos.';

/** Frase del jugador al IRSE sin comprar yerba. */
export const FRASE_AXELIKI_IRSE = 'Te vas sin yerba. Otra vez será.';

// -----------------------------------------------------------------------
// EL PROMO / EL AMIGO CIGARRO — persecución en patinete
// -----------------------------------------------------------------------

/** Frases con las que EL PROMO pide un cigarro. */
export const PROMO_FRASES_PIDE: { personajeId: 'el-promo'; texto: string }[] = [
  {
    personajeId: 'el-promo',
    texto: 'Eh, bro. ¿Tienes un cigarro? Solo uno. Uno de nada.',
  },
  {
    personajeId: 'el-promo',
    texto: 'Oye, oye. Para un segundo. Un cigarro, anda.',
  },
  {
    personajeId: 'el-promo',
    texto: 'Tío, que llevo sin fumar desde ayer. Un pitillo, porfa.',
  },
];

/** Frases con las que EL AMIGO CIGARRO pide un cigarro. */
export const AMIGO_CIGARRO_FRASES_PIDE: {
  personajeId: 'el-amigo-cigarro';
  texto: string;
}[] = [
  {
    personajeId: 'el-amigo-cigarro',
    texto: 'A ver, bro, ¿no te queda un cigarro? Es lo único que pido.',
  },
  {
    personajeId: 'el-amigo-cigarro',
    texto: 'Oye, ¿me das un cigarro? Te lo devuelvo mañana. Palabra.',
  },
  {
    personajeId: 'el-amigo-cigarro',
    texto: 'Eh, eh, que llevo sin fumar desde por la mañana. Un pitillo, anda.',
  },
];

/** Frase cuando el jugador le da un cigarro (simbólico, -1€). */
export const FRASE_DAR_CIGARRO = 'Le das un euro. Sabe que no es un cigarro. Se conforma igual.';

/** Frases de esquive — "acelerar el paso". */
export const FRASES_ESQUIVE: string[] = [
  'Aceleras el paso. La sombra del patinete se queda atrás.',
  'Tuerces a la primera esquina. El del patinete se queda gritándote algo.',
  'Aprovechas un semáforo en rojo para meter prisa. Te sigue una ronda más.',
  'Le dices que no y sigues caminando. Te mira raro.',
  'Sin mirar atrás. Otro día con tus cigarros.',
];

/** Frases de cuando el del patinete te sigue una ronda más después del
 *  esquive — el grupo del barrio pesado. */
export const FRASES_SIGUE_MAS: string[] = [
  'Aún se oye el patinete detrás. No se rinde.',
  '"¡Oye, que solo uno!" — le oyes a tu espalda, otra vez.',
  'El del patinete se acerca de nuevo. Paciencia.',
  'Vuelve a la carga. Una más.',
];

// -----------------------------------------------------------------------
// Helpers — elección aleatoria del pool correspondiente
// -----------------------------------------------------------------------

/** Elige una frase al azar del pool indicado. */
export function pickDe<T>(pool: readonly T[]): T {
  // Math.random con guard: pool vacío -> fallback.
  if (pool.length === 0) throw new Error('[barrio] pool vacío');
  return pool[Math.floor(Math.random() * pool.length)];
}

/** True con probabilidad `p` (0-1). */
export function roll(p: number): boolean {
  return Math.random() < p;
}

/** Elige al azar quién pide el cigarro: EL PROMO o EL AMIGO CIGARRO. */
export function perseguidorAleatorio(): 'el-promo' | 'el-amigo-cigarro' {
  return roll(0.5) ? 'el-promo' : 'el-amigo-cigarro';
}
